import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon, ZagButton, FormField } from '@zag/ui';
import { ApiKeyService } from './api-key.service';
import type { ApiKeyCreatedItem } from './api-key.service';

export type ApiKeyCreateDialogData = Record<string, never>;

/**
 * Two-step dialog for creating an API key.
 *
 * Step 1 — Form: user enters a name and selects an optional expiry.
 * Step 2 — Key display: plaintext key shown once with copy button and warning.
 *
 * On close after step 2, the dialog resolves with `{ id, name, start }` so the
 * parent list can update without re-fetching.
 * On cancel, resolves with `undefined`.
 *
 * @example
 * ```typescript
 * const ref = this.dialog.open(ApiKeyCreateDialogComponent, { data: {} });
 * ref.closed.subscribe(result => { if (result) this.apiKeyService.reload(); });
 * ```
 */
@Component({
  selector: 'sc-api-key-create-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onEscapeKey()' },
  imports: [FormsModule, TranslatePipe, ZagIcon, ZagButton, FormField],
  styles: [
    `
      .api-key-dialog {
        background: var(--background-color-elevated);
        border-radius: var(--border-radius-lg);
        padding: var(--spacing-xl);
        width: min(480px, 90vw);
        box-shadow: var(--shadow-lg);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-lg);
      }

      .api-key-dialog__title {
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .api-key-dialog__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .api-key-dialog__input {
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        background: var(--surface-color-default);
        color: var(--text-color-primary);
        font-size: var(--font-size-md);
      }

      .api-key-dialog__input:focus {
        outline: none;
        border-color: var(--color-primary);
      }

      .api-key-dialog__select {
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        background: var(--surface-color-default);
        color: var(--text-color-primary);
        font-size: var(--font-size-md);
        cursor: pointer;
      }

      .api-key-dialog__select:focus {
        outline: none;
        border-color: var(--color-primary);
      }

      .api-key-dialog__actions {
        display: flex;
        gap: var(--spacing-sm);
        justify-content: flex-end;
      }

      .api-key-dialog__warning {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        background: var(--color-warning-light);
        border: 1px solid var(--color-warning);
        border-radius: var(--border-radius-md);
        font-size: var(--font-size-sm);
        color: var(--text-color-primary);
      }

      .api-key-dialog__warning-icon {
        color: var(--color-warning);
        flex-shrink: 0;
      }

      .api-key-dialog__key-container {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        background: var(--background-color-secondary);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
      }

      .api-key-dialog__key-value {
        flex: 1;
        font-family: monospace;
        font-size: var(--font-size-sm);
        color: var(--text-color-primary);
        word-break: break-all;
        user-select: all;
      }

      .api-key-dialog__copy-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        padding: var(--spacing-xs) var(--spacing-sm);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-sm);
        background: var(--surface-color-default);
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
        cursor: pointer;
        white-space: nowrap;
        transition:
          background-color 0.15s,
          border-color 0.15s;
        flex-shrink: 0;
      }

      .api-key-dialog__copy-btn:hover {
        background: var(--color-primary-light);
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      .api-key-dialog__copy-btn--copied {
        background: var(--color-success-light);
        border-color: var(--color-success);
        color: var(--color-success);
      }

      .api-key-dialog__error {
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--color-error-light);
        border-radius: var(--border-radius-md);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }
    `,
  ],
  template: `
    <div class="api-key-dialog" role="dialog" aria-modal="true">
      @if (step() === 1) {
        <!-- Step 1: Form -->
        <h2 class="api-key-dialog__title">{{ 'apiKeys.create.title' | translate }}</h2>

        <div class="api-key-dialog__form">
          <zag-form-field [label]="'apiKeys.create.name' | translate">
            <input
              zagFormField
              class="api-key-dialog__input"
              type="text"
              [placeholder]="'apiKeys.create.namePlaceholder' | translate"
              [(ngModel)]="name"
              [attr.aria-label]="'apiKeys.create.name' | translate"
            />
          </zag-form-field>

          <zag-form-field [label]="'apiKeys.create.expiryLabel' | translate">
            <select
              zagFormField
              class="api-key-dialog__select"
              [(ngModel)]="expiresIn"
              [attr.aria-label]="'apiKeys.create.expiry' | translate"
            >
              <option [ngValue]="null">{{ 'apiKeys.create.expiry.never' | translate }}</option>
              <option [ngValue]="2592000">{{ 'apiKeys.create.expiry.30d' | translate }}</option>
              <option [ngValue]="7776000">{{ 'apiKeys.create.expiry.90d' | translate }}</option>
              <option [ngValue]="31536000">{{ 'apiKeys.create.expiry.1y' | translate }}</option>
            </select>
          </zag-form-field>
        </div>

        @if (error()) {
          <p class="api-key-dialog__error">{{ error() }}</p>
        }

        <div class="api-key-dialog__actions">
          <button type="button" zagButton zagType="secondary" (click)="onCancel()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" zagButton zagType="primary" [disabled]="!name.trim() || saving()" (click)="onCreate()">
            {{ saving() ? ('common.saving' | translate) : ('apiKeys.create.submit' | translate) }}
          </button>
        </div>
      } @else {
        <!-- Step 2: One-time key display -->
        <h2 class="api-key-dialog__title">{{ 'apiKeys.created.title' | translate }}</h2>

        <div class="api-key-dialog__warning">
          <span zagIcon="warning" class="api-key-dialog__warning-icon"></span>
          <span>{{ 'apiKeys.created.warning' | translate }}</span>
        </div>

        <div class="api-key-dialog__key-container">
          <span class="api-key-dialog__key-value">{{ createdItem()?.key }}</span>
          <button type="button" class="api-key-dialog__copy-btn" [class.api-key-dialog__copy-btn--copied]="copied()" (click)="onCopy()">
            <span [zagIcon]="copied() ? 'check' : 'content_copy'"></span>
            {{ copied() ? ('apiKeys.created.copied' | translate) : ('apiKeys.created.copy' | translate) }}
          </button>
        </div>

        <div class="api-key-dialog__actions">
          <button type="button" zagButton zagType="primary" (click)="onDone()">
            {{ 'common.done' | translate }}
          </button>
        </div>
      }
    </div>
  `,
})
export class ApiKeyCreateDialogComponent {
  private readonly dialogRef = inject(DialogRef<ApiKeyCreatedItem | undefined>);
  private readonly clipboard = inject(Clipboard);
  private readonly apiKeyService = inject(ApiKeyService);
  private readonly _data = inject<ApiKeyCreateDialogData>(DIALOG_DATA);

  readonly step = signal<1 | 2>(1);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly createdItem = signal<ApiKeyCreatedItem | null>(null);
  readonly copied = signal(false);

  name = '';
  expiresIn: number | null = null;

  onEscapeKey(): void {
    if (this.step() === 1) {
      this.onCancel();
    }
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onCreate(): void {
    const trimmedName = this.name.trim();
    if (!trimmedName) return;

    this.saving.set(true);
    this.error.set(null);

    this.apiKeyService.create({ name: trimmedName, expiresIn: this.expiresIn ?? undefined }).subscribe({
      next: response => {
        this.createdItem.set(response);
        this.step.set(2);
        this.saving.set(false);
      },
      error: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to create API key';
        this.error.set(message);
        this.saving.set(false);
      },
    });
  }

  onCopy(): void {
    const key = this.createdItem()?.key;
    if (!key) return;
    this.clipboard.copy(key);
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
  }

  onDone(): void {
    const item = this.createdItem();
    if (!item) {
      this.dialogRef.close(undefined);
      return;
    }
    // Return without the key — list shows only start prefix thereafter
    this.dialogRef.close({
      id: item.id,
      name: item.name,
      start: item.start,
      key: '', // intentionally empty — key must not persist in memory
      createdAt: item.createdAt,
    });
  }
}
